import { useTranslation } from 'react-i18next';
import { SlidersHorizontalIcon, XIcon, CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';

import styles from '@/modules/backoffice/pages/terminals/list/list.module.css';
import { TerminalListFiltersProps } from './terminal-list-filters.type';

export const TerminalListFilters = ({
  isOpen,
  isActive,
  openSections,
  draftFilters,
  onToggleOpen,
  onClose,
  onToggleSection,
  onDraftFiltersChange,
  onAddSerial,
  onRemoveSerial,
  onApply,
  onClearAll,
}: TerminalListFiltersProps) => {
  const { t } = useTranslation();

  const submitDisabled =
    !draftFilters.payfac.trim() &&
    !draftFilters.seller.trim() &&
    draftFilters.serialNumbers.length === 0;

  const handleSerialSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onAddSerial();
  };

  const toolbarBtnClass = {
    active: `${styles['toolbar-btn']} ${styles['toolbar-btn--active']}`,
    inactive: styles['toolbar-btn'],
  }[isActive ? 'active' : 'inactive'];

  const submitBtnClass = {
    enabled: styles['filter-panel-submit'],
    disabled: `${styles['filter-panel-submit']} ${styles['filter-panel-submit--disabled']}`,
  }[submitDisabled ? 'disabled' : 'enabled'];

  return (
    <div className={styles['filter-wrapper']}>
      <button
        type="button"
        className={toolbarBtnClass}
        onClick={onToggleOpen}
      >
        <SlidersHorizontalIcon size={16} />
        {t('backoffice.terminalsFiltersButton')}
      </button>

      {isOpen && (
        <div className={styles['filter-panel']}>
          <div className={styles['filter-panel-header']}>
            <span className={styles['filter-panel-title']}>
              {t('backoffice.terminalsFiltersTitle')}
            </span>
            <div className={styles['filter-panel-header-actions']}>
              {isActive && (
                <button
                  type="button"
                  className={styles['filter-panel-clear-btn']}
                  onClick={onClearAll}
                >
                  {t('backoffice.terminalsFiltersClear')}
                </button>
              )}
              <button
                type="button"
                className={styles['filter-panel-close-btn']}
                onClick={onClose}
              >
                <XIcon size={14} weight="bold" />
              </button>
            </div>
          </div>

          <div className={styles['filter-accordion']}>
            <div className={styles['filter-accordion-item']}>
              <button
                type="button"
                className={styles['filter-accordion-trigger']}
                onClick={() => onToggleSection('payfac')}
              >
                <span className={styles['filter-accordion-label']}>
                  {t('backoffice.terminalsFiltersPayfac')}
                </span>
                {openSections.payfac
                  ? <CaretUpIcon size={16} className={styles['filter-accordion-chevron']} />
                  : <CaretDownIcon size={16} className={styles['filter-accordion-chevron']} />
                }
              </button>
              {openSections.payfac && (
                <div className={styles['filter-accordion-content']}>
                  <input
                    className={styles['filter-input']}
                    placeholder={t('backoffice.terminalsFiltersTypeIdOrName')}
                    value={draftFilters.payfac}
                    onChange={(e) =>
                      onDraftFiltersChange((prev) => ({ ...prev, payfac: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className={styles['filter-accordion-item']}>
              <button
                type="button"
                className={styles['filter-accordion-trigger']}
                onClick={() => onToggleSection('seller')}
              >
                <span className={styles['filter-accordion-label']}>
                  {t('backoffice.terminalsFiltersSeller')}
                </span>
                {openSections.seller
                  ? <CaretUpIcon size={16} className={styles['filter-accordion-chevron']} />
                  : <CaretDownIcon size={16} className={styles['filter-accordion-chevron']} />
                }
              </button>
              {openSections.seller && (
                <div className={styles['filter-accordion-content']}>
                  <input
                    className={styles['filter-input']}
                    placeholder={t('backoffice.terminalsFiltersTypeIdOrName')}
                    value={draftFilters.seller}
                    onChange={(e) =>
                      onDraftFiltersChange((prev) => ({ ...prev, seller: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className={styles['filter-accordion-item']}>
              <button
                type="button"
                className={styles['filter-accordion-trigger']}
                onClick={() => onToggleSection('serialNumber')}
              >
                <span className={styles['filter-accordion-label']}>
                  {t('backoffice.terminalsFiltersSerialNumber')}
                </span>
                {openSections.serialNumber
                  ? <CaretUpIcon size={16} className={styles['filter-accordion-chevron']} />
                  : <CaretDownIcon size={16} className={styles['filter-accordion-chevron']} />
                }
              </button>
              {openSections.serialNumber && (
                <div className={styles['filter-accordion-content']}>
                  <form
                    className={styles['serial-filter-input-row']}
                    onSubmit={handleSerialSubmit}
                  >
                    <input
                      className={styles['filter-input']}
                      placeholder={t('backoffice.terminalsFiltersSerialPlaceholder')}
                      value={draftFilters.serialNumberInput}
                      onChange={(e) =>
                        onDraftFiltersChange((prev) => ({
                          ...prev,
                          serialNumberInput: e.target.value,
                        }))
                      }
                    />
                    <button
                      type="submit"
                      className={styles['serial-add-button']}
                      aria-label={t('backoffice.terminalsFiltersAddSerial')}
                    >
                      +
                    </button>
                  </form>
                  {draftFilters.serialNumbers.length > 0 && (
                    <div className={styles['serial-filter-chips']}>
                      {draftFilters.serialNumbers.map((serial) => (
                        <button
                          key={serial}
                          type="button"
                          className={styles['serial-filter-chip']}
                          onClick={() => onRemoveSerial(serial)}
                        >
                          {serial}
                          <span className={styles['serial-filter-chip-close']}>×</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles['filter-panel-footer']}>
            <button
              type="button"
              className={submitBtnClass}
              onClick={onApply}
              disabled={submitDisabled}
            >
              {t('backoffice.terminalsFiltersApply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
